import { useLayoutEffect, useState, type RefObject } from 'react';
import { useLocation, useNavigationType, type NavigateFunction } from 'react-router-dom';
import { capturePageFrom, pageFromTo, readPageFrom, reopenSearchState } from './pageFrom';
import { WPPost } from '../types/wordpress';

export type PlaceDirection = 'deeper' | 'shallower';
export type PlaceStep = PlaceDirection | 'leave';
export type PlaceMotion = PlaceDirection | 'grow' | 'shrink' | 'fade' | 'replace';

export interface PlaceSurface {
  kind: 'program' | 'hub' | 'resource';
  slug: string;
  /** Distinguishes a hub row from a hub card when both are on the page. */
  via?: string;
}

export interface PlaceCrumb {
  label: string;
  to?: string;
  state?: unknown;
  step?: PlaceStep;
}

/** Room shown above a document while its hub context is still loading. */
export interface PlaceRoom {
  title: string;
  to: string;
  backTo: string;
  backLabel: string;
  crumbs: PlaceCrumb[];
}

const DIRECTION_KEY = 'place-motion';
const ROOM_KEY = 'place-room';
const SURFACE_KEY = 'place-surface';

let pendingRoom: PlaceRoom | null = null;
let viewTransitionActive = false;
let surfaceClaimed = false;
let lastHistoryIdx: number | null = null;
const resourcePosts = new Map<string, WPPost>();

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function historyIndex() {
  const state = window.history.state as { idx?: unknown } | null;
  return state && typeof state.idx === 'number' ? state.idx : 0;
}

function noteHistoryIndex(): PlaceDirection | null {
  const idx = historyIndex();
  const previous = lastHistoryIdx;
  lastHistoryIdx = idx;
  if (previous === null || idx === previous) return null;
  return idx < previous ? 'shallower' : 'deeper';
}

type ViewTransitionLike = { finished: Promise<void> };

function viewTransitionStart(): ((update: () => void | Promise<void>) => ViewTransitionLike) | null {
  const start = (document as Document & {
    startViewTransition?: (callback: {
      update: () => void | Promise<void>;
    }) => ViewTransitionLike;
  }).startViewTransition;
  if (typeof start !== 'function') return null;
  return (update) => start.call(document, { update });
}

function placeHeadingReady(before: string, motion: PlaceMotion) {
  const next = document.querySelector('h1')?.textContent ?? '';
  if (!next || next === before) return false;
  const page = document.querySelector('[data-place-page]');
  if (page?.querySelector('.animate-pulse')) return false;
  if (motion !== 'shrink') return true;
  return Boolean(document.querySelector('.place-surface:not([data-place-page])'));
}

function focusPlaceHeading() {
  const selectors = [
    '[data-place-focus]',
    '#entered-hub-title',
    '#resource-title',
    '[data-place-page] h1',
    'main h1',
  ];
  const heading = selectors
    .map((selector) => document.querySelector<HTMLElement>(selector))
    .find((element): element is HTMLElement => Boolean(element));
  if (!heading) return;
  if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
  heading.focus({ preventScroll: true });
}

/** Focus the heading this step landed on, without scrolling away from it. */
export function markPlaceFocus(heading: HTMLElement | null) {
  document.querySelectorAll('[data-place-focus]').forEach((node) => {
    node.removeAttribute('data-place-focus');
  });
  if (!heading) return;
  if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
  heading.setAttribute('data-place-focus', '');
  heading.focus({ preventScroll: true });
}

function settlePlaceScroll(motion: PlaceMotion) {
  const hash = window.location.hash;
  const hubPlace = hash.startsWith('#hub-') && !hash.startsWith('#hub-section-');
  if (hubPlace || ((motion === 'deeper' || motion === 'grow' || motion === 'replace' || motion === 'fade') && !hash)) {
    window.scrollTo(0, 0);
  }
}

/** The router commits on a transition, after startViewTransition's callback returns. */
function waitForNextHeading(update: () => void, motion: PlaceMotion): Promise<void> {
  const before = document.querySelector('h1')?.textContent ?? '';
  update();
  const readyNow = () => placeHeadingReady(before, motion);
  if (readyNow()) {
    settlePlaceScroll(motion);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      settlePlaceScroll(motion);
      resolve();
    };
    const headingChanged = () => placeHeadingReady(before, motion);
    const observer = new MutationObserver(() => {
      if (headingChanged()) finish();
    });
    observer.observe(document.getElementById('root') ?? document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    const started = performance.now();
    const poll = () => {
      if (headingChanged() || performance.now() - started > 1500) finish();
      else requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  });
}

export function isPlainClick(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function clearPlaceSurfaceClass() {
  document.querySelectorAll('.place-surface').forEach((el) => el.classList.remove('place-surface'));
}

export function beginPlaceTransition(direction: PlaceMotion, update: () => void) {
  document.documentElement.dataset.placeMotion = direction;
  const releaseMotion = () => {
    if (document.documentElement.dataset.placeMotion === direction) {
      delete document.documentElement.dataset.placeMotion;
    }
  };

  if (prefersReducedMotion()) {
    update();
    focusPlaceHeading();
    releaseMotion();
    return;
  }

  try {
    sessionStorage.setItem(DIRECTION_KEY, direction);
  } catch {
    // The destination still renders when storage is unavailable.
  }

  const start = viewTransitionStart();
  if (!start) {
    update();
    focusPlaceHeading();
    releaseMotion();
    return;
  }

  surfaceClaimed = false;
  if (direction === 'shrink') {
    document.querySelector('[data-place-page]')?.classList.add('place-surface');
  }
  viewTransitionActive = true;
  let applied = false;
  const finish = () => {
    viewTransitionActive = false;
    if (document.documentElement.dataset.placeMotion === direction) {
      delete document.documentElement.dataset.placeMotion;
    }
    clearPlaceSurfaceClass();
  };
  try {
    const transition = start(() => {
      applied = true;
      return waitForNextHeading(update, direction);
    });
    transition.finished.finally(() => {
      focusPlaceHeading();
      finish();
    });
  } catch {
    finish();
    if (!applied) {
      update();
      focusPlaceHeading();
    }
  }
}

function isPlaceSurface(value: unknown): value is PlaceSurface {
  if (!value || typeof value !== 'object') return false;
  const surface = value as PlaceSurface;
  return (
    (surface.kind === 'program' || surface.kind === 'hub' || surface.kind === 'resource') &&
    typeof surface.slug === 'string' &&
    surface.slug.length > 0 &&
    (surface.via === undefined || typeof surface.via === 'string')
  );
}

export function rememberPlaceSurface(surface: PlaceSurface | null) {
  try {
    if (surface) sessionStorage.setItem(SURFACE_KEY, JSON.stringify(surface));
    else sessionStorage.removeItem(SURFACE_KEY);
  } catch {
    // Shrink falls back to a fade when the card cannot be remembered.
  }
}

export function peekPlaceSurface(): PlaceSurface | null {
  try {
    const raw = sessionStorage.getItem(SURFACE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isPlaceSurface(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function readPlaceSurface(state: unknown): PlaceSurface | null {
  if (!state || typeof state !== 'object') return null;
  const surface = (state as { placeSurface?: unknown }).placeSurface;
  return isPlaceSurface(surface) ? surface : null;
}

export function beginPlaceOpen(
  origin: HTMLElement | null,
  surface: PlaceSurface,
  to: string,
  navigate: NavigateFunction,
  location: { pathname: string; search: string },
  alongside?: () => void,
  fromExtras?: { reopenSearch?: string }
) {
  rememberPlaceSurface(surface);
  const from = capturePageFrom(location);
  if (fromExtras?.reopenSearch) {
    from.label = 'Back to search';
    from.reopenSearch = fromExtras.reopenSearch;
  }
  origin?.classList.add('place-surface');
  beginPlaceTransition('grow', () => {
    navigate(to, { state: { from, placeSurface: surface } });
    alongside?.();
  });
}

export function openPlace(
  event: {
    button: number;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    preventDefault: () => void;
  },
  origin: HTMLElement | null,
  surface: PlaceSurface,
  to: string,
  navigate: NavigateFunction,
  location: { pathname: string; search: string }
) {
  if (!isPlainClick(event)) return false;
  event.preventDefault();
  beginPlaceOpen(origin, surface, to, navigate, location);
  return true;
}

export function closePlace(
  event: {
    button: number;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    preventDefault: () => void;
  },
  to: string,
  navigate: NavigateFunction,
  surface: PlaceSurface | null,
  state?: unknown
) {
  if (!isPlainClick(event)) return false;
  event.preventDefault();
  const fade = !surface || surface.via === 'search';
  if (fade) rememberPlaceSurface(null);
  else rememberPlaceSurface(surface);
  beginPlaceTransition(fade ? 'fade' : 'shrink', () => {
    navigate(to, state ? { state } : undefined);
  });
  return true;
}

/** Breadcrumb and in-program links take the same step as Back, the room name, or a hub. */
export function followPlaceStep(
  event: {
    button: number;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    preventDefault: () => void;
  },
  step: PlaceStep,
  to: string,
  navigate: NavigateFunction,
  location: { pathname: string; search: string; state: unknown },
  surface: PlaceSurface | null
) {
  if (step === 'leave') {
    const from = readPageFrom(location.state);
    const entrance = from ? pageFromTo(from) : null;
    const sameScreen = entrance === to || from?.pathname === to;
    closePlace(event, to, navigate, sameScreen ? surface : null, reopenSearchState(from));
    return;
  }
  if (!isPlainClick(event)) return;
  event.preventDefault();
  const current =
    location.state && typeof location.state === 'object'
      ? { ...(location.state as Record<string, unknown>) }
      : {};
  delete current.restoreScroll;
  beginPlaceTransition(step, () => {
    navigate(to, { preventScrollReset: true, state: current });
  });
}

/** The list paints this on the card that opened the place, before the new snapshot. */
export function usePlaceOrigin(
  ref: RefObject<HTMLElement | null>,
  kind: PlaceSurface['kind'],
  slug: string,
  via?: string
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || surfaceClaimed) return;
    if (document.documentElement.dataset.placeMotion !== 'shrink') return;
    const surface = peekPlaceSurface();
    if (!surface || surface.kind !== kind || surface.slug !== slug) return;
    if (surface.via && surface.via !== via) return;
    surfaceClaimed = true;
    el.classList.add('place-surface');
    const rect = el.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      el.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
  });
}

/** The destination page shares its box with the card that opened it. */
export function usePlacePage(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.documentElement.dataset.placeMotion !== 'grow') return;
    el.classList.add('place-surface');
  });
}

function consumePlaceDirection(): PlaceDirection | null {
  try {
    const value = sessionStorage.getItem(DIRECTION_KEY);
    sessionStorage.removeItem(DIRECTION_KEY);
    if (value === 'deeper' || value === 'shallower') return value;
  } catch {
    return null;
  }
  return null;
}

/** Enter animation when the View Transition API did not run. */
export function usePlaceSlide(): PlaceDirection | null {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [slide, setSlide] = useState<PlaceDirection | null>(null);

  useLayoutEffect(() => {
    const popped = noteHistoryIndex();
    const recorded = consumePlaceDirection();
    if (prefersReducedMotion() || viewTransitionActive) return;
    const direction = navigationType === 'POP' ? popped : recorded;
    if (direction) setSlide(direction);
  }, [location.key, navigationType]);

  useLayoutEffect(() => {
    if (!slide) return;
    const id = window.setTimeout(() => setSlide(null), 320);
    return () => window.clearTimeout(id);
  }, [slide]);

  return slide;
}

export function placeEnterClass(slide: PlaceDirection | null) {
  if (slide === 'deeper') return 'place-enter-deeper';
  if (slide === 'shallower') return 'place-enter-shallower';
  return '';
}

export function rememberResourcePost(post: WPPost) {
  if (!post.slug || !post.content?.rendered) return;
  resourcePosts.set(post.slug, post);
}

export function peekResourcePost(slug: string | undefined): WPPost | null {
  if (!slug) return null;
  return resourcePosts.get(slug) ?? null;
}

export function rememberPlaceRoom(room: PlaceRoom | null) {
  pendingRoom = room;
  try {
    if (room) sessionStorage.setItem(ROOM_KEY, JSON.stringify(room));
    else sessionStorage.removeItem(ROOM_KEY);
  } catch {
    // The bar can still appear once the hub request returns.
  }
}

export function peekPlaceRoom(): PlaceRoom | null {
  if (pendingRoom) return pendingRoom;
  try {
    const raw = sessionStorage.getItem(ROOM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlaceRoom;
    if (!parsed || typeof parsed.title !== 'string' || typeof parsed.to !== 'string') return null;
    if (typeof parsed.backTo !== 'string' || typeof parsed.backLabel !== 'string') return null;
    if (!Array.isArray(parsed.crumbs)) return null;
    pendingRoom = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export interface ReplacedDocument {
  to: string;
  title: string;
  state: unknown;
  room: PlaceRoom | null;
}

function isPlaceRoom(value: unknown): value is PlaceRoom {
  if (!value || typeof value !== 'object') return false;
  const room = value as PlaceRoom;
  return (
    typeof room.title === 'string' &&
    typeof room.to === 'string' &&
    typeof room.backTo === 'string' &&
    typeof room.backLabel === 'string' &&
    Array.isArray(room.crumbs)
  );
}

/** The document a same-depth related link replaced. Back returns here. */
export function readReplacedDocument(state: unknown): ReplacedDocument | null {
  if (!state || typeof state !== 'object') return null;
  const value = (state as { replacedDocument?: unknown }).replacedDocument;
  if (!value || typeof value !== 'object') return null;
  const doc = value as { to?: unknown; title?: unknown; state?: unknown; room?: unknown };
  if (typeof doc.to !== 'string' || !doc.to.startsWith('/')) return null;
  if (typeof doc.title !== 'string' || !doc.title.trim()) return null;
  return {
    to: doc.to,
    title: doc.title,
    state: doc.state,
    room: isPlaceRoom(doc.room) ? doc.room : null,
  };
}

export function roomForHub(room: PlaceRoom | null, hubSlug: string | null): PlaceRoom | null {
  if (!room || !hubSlug) return null;
  const hubHash = `#hub-${hubSlug}`;
  const hubPath = `/hubs/${hubSlug}`;
  const targets = [room.to, room.backTo, ...room.crumbs.map((crumb) => crumb.to ?? '')];
  const matches = targets.some(
    (value) => value.includes(hubHash) || value === hubPath || value.startsWith(`${hubPath}?`) || value.startsWith(`${hubPath}#`)
  );
  return matches ? room : null;
}
